'use client';

import { datePickerTriggerVariants } from '@zealot/ui/components/date-picker';
import { Input } from '@zealot/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@zealot/ui/components/popover';
import { cn } from '@zealot/ui/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { ClockIcon } from 'lucide-react';
import * as React from 'react';

const timePickerTriggerVariants = datePickerTriggerVariants;

type TimeSlot = {
  value: string;
  label: string;
};

const generateTimeSlots = (interval: number): TimeSlot[] => {
  const safeInterval = !Number.isFinite(interval) || interval <= 0 ? 15 : interval;
  const slots: TimeSlot[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += safeInterval) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const value = `${h}:${m}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${m} ${ampm}`;
      slots.push({ value, label });
    }
  }
  return slots;
};

type TimePickerProps = {
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  interval?: number;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
} & VariantProps<typeof timePickerTriggerVariants>;

// gloss
const TimePicker = ({
  value,
  onValueChange,
  placeholder = 'Select time',
  interval = 15,
  disabled = false,
  size = 'default',
  isInvalid = false,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: TimePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const slots = React.useMemo(() => generateTimeSlots(interval), [interval]);

  const filteredSlots = React.useMemo(
    () => slots.filter((slot) => slot.label.toLowerCase().includes(search.toLowerCase())),
    [slots, search],
  );

  const displayValue = React.useMemo(() => {
    if (!value) return null;
    const slot = slots.find((s) => s.value === value);
    return slot?.label ?? value;
  }, [value, slots]);

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      setSearch('');
      setHighlightedIndex(-1);
    }
  }, []);

  const handleSelect = React.useCallback(
    (slotValue: string) => {
      onValueChange?.(slotValue);
      setIsOpen(false);
      setSearch('');
    },
    [onValueChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredSlots.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredSlots.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = highlightedIndex >= 0 ? filteredSlots[highlightedIndex] : filteredSlots[0];
        if (target) handleSelect(target.value);
      }
    },
    [filteredSlots, highlightedIndex, handleSelect],
  );

  React.useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !value || !listRef.current) return;
    const timer = setTimeout(() => {
      const selectedEl = listRef.current?.querySelector(`[data-value="${value}"]`);
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen, value]);

  React.useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-highlighted="true"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        data-slot="time-picker-trigger"
        id={id}
        disabled={disabled}
        aria-invalid={isInvalid || ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn(timePickerTriggerVariants({ size, className }))}
        render={<button type="button" />}
      >
        <span data-slot="time-picker-value" className={cn(!value && 'text-muted-foreground')}>
          {displayValue ?? placeholder}
        </span>
        <ClockIcon className="text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div data-slot="time-picker-content">
          <div data-slot="time-picker-search" className="border-border border-b p-1.5">
            <Input
              ref={searchInputRef}
              type="text"
              size="sm"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlightedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Type to filter..."
              className="border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>
          <div
            ref={listRef}
            data-slot="time-picker-list"
            className="max-h-56 overflow-y-auto overscroll-contain p-1"
            role="listbox"
          >
            {filteredSlots.length === 0 ? (
              <div data-slot="time-picker-empty" className="px-2 py-6 text-center text-muted-foreground text-sm">
                No times found
              </div>
            ) : (
              filteredSlots.map((slot, index) => (
                <button
                  key={slot.value}
                  type="button"
                  role="option"
                  data-slot="time-picker-option"
                  data-value={slot.value}
                  data-highlighted={index === highlightedIndex || undefined}
                  aria-selected={value === slot.value}
                  onClick={() => handleSelect(slot.value)}
                  className={cn(
                    'flex w-full cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none md:text-xs',
                    'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
                    value === slot.value && 'font-medium text-primary',
                  )}
                >
                  {slot.label}
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export type { TimePickerProps };
export { TimePicker, timePickerTriggerVariants };
